import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Private } from '@shared/components/common/Private';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth/AuthProvider';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useLanguage } from '@core/i18n';
import { saveReservationNote, saveCompanions } from '@features/booking/api/reservations';
import { CompanionInputs } from '@features/booking/components/CompanionInputs';
import { GuestDetailsCard } from '@features/booking/components/GuestDetailsCard';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { useRegularCompanions } from '@features/booking/hooks/useRegularCompanions';
import { resolvePairingName } from '@features/booking/utils/pairings';
import { sanitizeHtml } from '@shared/utils/sanitize';
import type { CompanionData } from '@features/booking/api/reservations';
import type { RegularCompanion } from '@features/booking/api/companions';

const OCCASIONS = [
  { value: 'birthday', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_1' },
  { value: 'anniversary', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_2' },
  { value: 'business', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_3' },
  { value: 'trip', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_4' },
  { value: 'thankyou', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_5' },
  { value: 'gift', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_6' },
] as const;

const SERVICE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
] as const;

const BookingFinalPage = () => {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { currentLanguage } = useLanguage();
  const { trackEvent } = useAnalytics();

  const { data: regularCompanions } = useRegularCompanions();

  const [note, setNote] = useState('');
  const [occasion, setOccasion] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [languageService, setLanguageService] = useState('');
  const [companions, setCompanions] = useState<CompanionData[]>([]);
  const [availableRegulars, setAvailableRegulars] = useState<RegularCompanion[]>([]);
  const [regularsInitialized, setRegularsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_COMPLETE });
  }, [trackEvent]);

  // CRITICAL guard: never display the confirmation surface unless a real
  // reservation was committed. `booking.reservationId` is set inside the
  // commit path (commitReservation.ts → setReservationResult) BEFORE
  // navigation to /booking-final, so a valid landing always has it.
  //
  // Without this guard, /booking-final renders whatever booking state is
  // in sessionStorage — letting users see a "Reserva Confirmada" surface
  // for a reservation that does NOT exist in the backend. Restaurants then
  // receive guests whose reservations they have no record of. (Reported by
  // ops 2026-05-25 after Beast Butchers saw the fake surface.)
  //
  // Reachable when:
  //   - Direct URL access / bookmark / autocomplete to /booking-final
  //   - Browser back-forward to a stale /booking-final history entry
  //   - Any commit path that navigates to /booking-final before
  //     reservationId is written to booking state (shouldn't happen with
  //     flushSync but guarded defensively)
  //
  // Skip the redirect while BookingProvider is still hydrating
  // (`isLoading=true`) — sessionStorage read is synchronous and resolves
  // in the same microtask as mount, so this is at most a single-frame
  // skip in practice (same pattern as PaymentPage's hydration gate).
  const guardFiredRef = useRef(false);
  useEffect(() => {
    if (booking.isLoading) return;
    if (booking.reservationId) return;
    if (guardFiredRef.current) return;
    guardFiredRef.current = true;

    Sentry.captureMessage('BookingFinalPage rendered without reservationId', {
      level: 'warning',
      tags: {
        guard: 'booking-final-no-reservation-id',
        'restaurant.slug': restaurant.subdomainPrefix,
      },
      extra: {
        // Diagnostic snapshot — what state the failing user actually has,
        // so we can tell apart direct-URL access, browser-back-to-stale,
        // and any commit-path regression. PII-safe by construction
        // (booleans, IDs, counts only).
        diagnosticState: {
          diners: booking.diners,
          selectedDate: booking.selectedDate,
          selectedTime: booking.selectedTime,
          service: booking.service,
          guaranteeType: booking.guaranteeType,
          feeDeposit: booking.feeDeposit,
          flowType: booking.flowType,
          hasCardToken: !!booking.cardToken,
          startedAt: booking.startedAt,
          ageMs: booking.startedAt ? Date.now() - booking.startedAt : null,
          referrer: typeof document !== 'undefined' ? !!document.referrer : null,
        },
        restaurantId: restaurant.id,
      },
    });
    navigate('/', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.isLoading, booking.reservationId]);

  // Initialize available regular companions once from query data
  useEffect(() => {
    if (regularCompanions.length > 0 && !regularsInitialized) {
      setAvailableRegulars(regularCompanions);
      setRegularsInitialized(true);
    }
  }, [regularCompanions, regularsInitialized]);

  const { dateLocale } = useLanguage();

  const isWaitingList = booking.flowType === 'waiting_list';
  const isEarlyAccess = booking.isEarlyAccessFlow;

  const displayCurrency = restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';
  const formattedDate = booking.selectedDate
    ? format(new Date(booking.selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy', { locale: dateLocale })
    : '';

  const userDisplayName = !booking.isAgentReservation ? (authState.name ?? '').trim() : '';

  const hasPreferences = !!(booking.allergies || booking.veggie || booking.kids || booking.pets || booking.accessibility);
  const hasPairings = booking.selectedPairings.length > 0;
  const hasGuarantee = booking.guaranteeType !== 'traditional' && booking.feeDeposit > 0;

  const toleranceText = (isWaitingList ? t('GRAB_CONFIRMATION_WL_TOLERANCE') : t('GRAB_CONFIRMATION_TOLERANCE')).trim();

  // Render-time guard counterpart of the guard useEffect above. Returns
  // null so the user does NOT briefly see the fake confirmation surface
  // between mount and the redirect firing in the effect. While
  // BookingProvider is still hydrating from sessionStorage
  // (`isLoading=true`), defer judgment — same pattern as PaymentPage.
  // Placed AFTER all hook calls to respect React's rules-of-hooks.
  if (booking.isLoading) return null;
  if (!booking.reservationId) return null;

  const showOccasion = !!restaurant.allowOccasion;
  const showPromoCode = !!restaurant.allowPromoCode;
  const showServiceLanguage = !!restaurant.allowServiceLanguage;
  const showCompanions = !!restaurant.allowCompanions && booking.diners > 1;
  const maxCompanions = Math.max(0, booking.diners - 1);

  const handleSelectRegular = (regular: RegularCompanion) => {
    if (companions.length >= maxCompanions) return;
    setCompanions((prev) => [
      ...prev,
      {
        name: regular.name,
        email: regular.email,
        phone: regular.phone,
        id: regular.id,
        isRegular: true,
        notes: regular.notes,
        treatment: regular.treatment,
        relationship: regular.relationship,
      },
    ]);
    setAvailableRegulars((prev) => prev.filter((r) => r.id !== regular.id));
  };

  const handleCompanionRemoved = (_index: number, companion: CompanionData) => {
    if (companion.isRegular && companion.id) {
      const original = regularCompanions.find((r) => r.id === companion.id);
      if (original) {
        setAvailableRegulars((prev) => [...prev, original]);
      }
    }
  };

  const handleFinish = async () => {
    if (!booking.reservationId) {
      booking.resetBooking();
      navigate('/');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save note and companions in parallel
      const promises: Promise<void>[] = [
        saveReservationNote(currentLanguage, {
          resId: booking.reservationId,
          note: note.trim(),
          occasion: occasion || undefined,
          languageService: languageService || undefined,
          promoCode: promoCode.trim() || undefined,
        }),
      ];

      // Save companions if any have names
      const validCompanions = companions.filter((c) => c.name.trim());
      if (validCompanions.length > 0) {
        promises.push(
          saveCompanions({
            reservationId: booking.reservationId,
            companions: validCompanions,
          }),
        );
      }

      await Promise.all(promises);

      booking.setUserNote(note.trim());

      toast.success(t('GRAB_CONFIRMATION_SAVED'), {
        icon: <Check className="w-4 h-4" />,
        duration: 2000,
      });

      // Brief delay so the user sees the success toast before navigating away
      await new Promise((resolve) => setTimeout(resolve, 1500));

      booking.resetBooking();
      navigate('/');
    } catch {
      setError(t('GRAB_CONFIRMATION_SAVE_ERROR'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BookingPageLayout
      title=""
      progress={100}
      showBack={false}
    >
      <div className="w-full sm:px-6 flex flex-col items-center space-y-6">
        {/* Success / WL Icon */}
        <div data-testid="booking-success" className="animate-fade-in">
          {isWaitingList || isEarlyAccess ? (
            <Clock className="w-20 h-20 text-amber-500" strokeWidth={1.5} />
          ) : (
            <CheckCircle className="w-20 h-20 text-green-500" strokeWidth={1.5} />
          )}
        </div>

        <h2 className="text-3xl font-display text-center">
          {isWaitingList
            ? t('WAITING_LIST_MODAL_CONFIRMATION_DIALOG_TITLE')
            : isEarlyAccess
              ? t('EARLY_ACCESS_CONFIRMED')
              : t('GRAB_CONFIRMATION_TITLE')}
        </h2>

        {booking.uniqueId && (
          <div data-testid="reservation-id" className="bg-foreground/5 rounded-xl p-4 text-center">
            <p className="text-sm text-foreground/50">{t('GRAB_CONFIRMATION_RESERVATION_ID')}</p>
            <p className="text-2xl font-semibold text-foreground tracking-wider mt-1">
              {booking.uniqueId}
            </p>
          </div>
        )}

        {isWaitingList && booking.wlDates.length > 0 && (
          <div className="bg-foreground/5 rounded-xl p-4 w-full space-y-2">
            <p className="text-sm text-foreground/50">{t('BOOKING_FINAL_REQUESTED_DATES')}</p>
            <div className="flex flex-wrap gap-2">
              {booking.wlDates.map((d) => (
                <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-3 py-1 rounded-full">
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* The agent branch interpolates `booking.guestEmail` directly into
            the translation string — bare PII in DOM text. Wrap the whole
            paragraph so session-replay tools never capture it. The other
            branches are generic copy; masking them is harmless. */}
        <Private>
          <p className="text-foreground/50 text-sm text-center">
            {isWaitingList
              ? t('BOOKING_FINAL_WAITING_LIST_NOTICE')
              : isEarlyAccess
                ? t('EARLY_ACCESS_REQUESTED_MESSAGE')
                : booking.isAgentReservation && booking.guestEmail
                  ? t('GRAB_CONFIRMATION_AGENT_EMAIL_SENT', { email: booking.guestEmail })
                  : t('GRAB_CONFIRMATION_EMAIL_SENT')}
          </p>
        </Private>

        {/* Reservation Summary */}
        <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
          <h3 className="font-display text-lg font-semibold text-foreground">
            {t('SUMMARY_YOUR_RESERVATION')}
          </h3>
          {userDisplayName && (
            <Private>
              <SummaryRow label={t('profile:MY_ACCOUNT_LABEL_NAME')} value={userDisplayName} />
            </Private>
          )}
          <SummaryRow label={t('SUMMARY_GUESTS')} value={t('PARTYSIZE_LABEL', { count: booking.diners })} />
          {isWaitingList && booking.wlDates.length > 0 ? (
            <div className="flex justify-between items-start">
              <span className="text-sm text-foreground/50">{t('SUMMARY_DATE')}</span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                {booking.wlDates.map((d) => (
                  <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-2 py-0.5 rounded">
                    {format(new Date(d + 'T12:00:00'), 'MMM d', { locale: dateLocale })}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <SummaryRow label={t('SUMMARY_DATE')} value={formattedDate} />
          )}
          {booking.selectedTime && (
            <SummaryRow label={t('SUMMARY_TIME')} value={booking.selectedTime} />
          )}
          {booking.service && (
            <SummaryRow label={t('SUMMARY_SERVICE')} value={t(`FILTER_STRING_${booking.service.toUpperCase()}`)} />
          )}
          {booking.selectedExperience && (
            <SummaryRow
              label={t('SUMMARY_EXPERIENCE')}
              value={
                restaurant.menus?.[currentLanguage]?.find(
                  (m: { id: number; name: string }) => String(m.id) === booking.selectedExperience!.id
                )?.name ?? booking.selectedExperience.title
              }
            />
          )}
        </div>

        {/* Preferences Summary */}
        {hasPreferences && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PREFERENCES')}
            </h3>
            {booking.allergies && (
              <Private>
                <SummaryRow label={t('STRING_MENU_ALLERGIES_TITLE')} value={booking.allergies} />
              </Private>
            )}
            {booking.veggie && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_VEGETARIAN')} value={String(booking.howManyVeggie)} />
            )}
            {booking.kids && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_CHILDREN')} value={String(booking.howManyKids)} />
            )}
            {booking.pets && (
              <SummaryRow label={t('NEW_MODAL_PET')} value={t('STRING_YES')} />
            )}
            {booking.accessibility && (
              <SummaryRow label={t('NEW_MODAL_ACCESSIBILITY_TABLE')} value={t('STRING_YES')} />
            )}
          </div>
        )}

        {/* Pairings Summary */}
        {hasPairings && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PAIRINGS')}
            </h3>
            {booking.selectedPairings.map((p) => {
              const resolvedName = resolvePairingName(
                p.id,
                p.name,
                restaurant.beveragePairings,
                currentLanguage,
              );
              return (
                <SummaryRow
                  key={p.id}
                  label={`${resolvedName} (x${p.quantity})`}
                  value={`${displayCurrency} ${(p.price * p.quantity).toLocaleString()}`}
                />
              );
            })}
          </div>
        )}

        {/* Agent: Guest Info Summary */}
        {booking.isAgentReservation && booking.guestName && (
          <GuestDetailsCard
            name={booking.guestName}
            email={booking.guestEmail}
            phone={booking.guestPhone}
            className="w-full"
          />
        )}

        {/* Guarantee Info */}
        {hasGuarantee && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-2 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_GUARANTEE')}
            </h3>
            <p className="text-sm text-foreground/70">
              {booking.guaranteeType === 'deposit'
                ? t('CONFIRMATION_DEPOSIT_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })
                : t('CONFIRMATION_NO_SHOW_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })}
            </p>
          </div>
        )}

        {/* Tolerance Notice — rendered via sanitizeHtml because restaurants
            can override GRAB_CONFIRMATION_TOLERANCE in their appTexts with
            HTML markup (e.g. <p class="..."><br/>...). The previous
            `<p>{toleranceText}</p>` plain-text rendering showed the raw
            tags to users (reported 2026-05-25, Beast Butchers). Wrapper is
            <div> not <p> so HTML containing block-level tags doesn't
            produce nested-<p> auto-closing in the browser. */}
        {toleranceText && (
          <div className="flex items-start gap-2 w-full text-sm text-foreground/50">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div
              className="flex-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(toleranceText) }}
            />
          </div>
        )}

        {/* Special Requirements / Notes — free-form user input. Users
            commonly include names, allergies, anniversaries, kids' info.
            Wrap so session-replay tools don't capture this high-entropy
            PII (allergies + dietary info is also health-adjacent). */}
        <Private className="w-full space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('NEW_MODAL_SPECIAL_REQUIREMENTS')}
          </label>
          <textarea
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none resize-none"
            rows={3}
            placeholder={t('NEW_MODAL_WRITE_A_NOTE_HERE')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Private>

        {/* Companions */}
        {showCompanions && (
          <div className="w-full space-y-3">
            {availableRegulars.length > 0 && (
              <div className={`space-y-1 ${companions.length >= maxCompanions ? 'opacity-50' : ''}`}>
                <label className="text-sm font-medium text-foreground/70">
                  {t('GRAB_CONFIRMATION_ADD_REGULAR_GUEST')}
                </label>
                <p className="text-sm text-foreground/60">
                  {availableRegulars.map((regular, idx) => (
                    <span key={regular.id}>
                      <button
                        type="button"
                        disabled={companions.length >= maxCompanions}
                        onClick={() => handleSelectRegular(regular)}
                        className="underline text-brand hover:text-brand-dark transition-colors disabled:cursor-not-allowed disabled:text-foreground/40"
                      >
                        {regular.name}
                      </button>
                      {idx < availableRegulars.length - 1 && ', '}
                    </span>
                  ))}
                </p>
              </div>
            )}

            <CompanionInputs
              maxCompanions={maxCompanions}
              companions={companions}
              onChange={setCompanions}
              onRemove={handleCompanionRemoved}
            />
          </div>
        )}

        {/* Occasion */}
        {showOccasion && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_SPECIAL_OCCASION')}
            </label>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`px-4 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    occasion === o.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setOccasion(occasion === o.value ? '' : o.value)}
                >
                  {t(o.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Promo Code */}
        {showPromoCode && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('PROMOCODE_LABEL')}
            </label>
            <Input
              type="text"
              placeholder={t('PROMOCODE_PLACEHOLDER')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="rounded-xl"
            />
          </div>
        )}

        {/* Service Language */}
        {showServiceLanguage && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_HOST_LANGUAGE')}
            </label>
            <div className="flex gap-2">
              {SERVICE_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  className={`px-5 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    languageService === lang.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setLanguageService(languageService === lang.value ? '' : lang.value)}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm w-full">
            {error}
          </div>
        )}
      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="finish-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleFinish}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('GRAB_CONFIRMATION_SAVING') : t('CONFIRMATION_MESSAGE_RESERVATION_TEXT_10')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default BookingFinalPage;
